'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { entitiesApi } from '@/lib/api/endpoints';
import { useSession } from '@/lib/auth-client';
import { emailValidation } from '@/lib/utils/validation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface DirectoryListingFormProps {
  /** Called after the request is sent successfully. */
  onSuccess?: () => void;
  className?: string;
}

interface DirectoryListingFormData {
  businessName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  category: string;
  description: string;
}

/**
 * Business-details form for requesting a directory listing. Submissions are
 * emailed to the platform directory inbox via
 * {@link entitiesApi.sendDirectoryListingEnquiry}. Shared by the
 * {@link DirectoryListingDialog} and the /directory/get-listed page.
 */
export function DirectoryListingForm({ onSuccess, className }: DirectoryListingFormProps) {
  const { data: session } = useSession();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DirectoryListingFormData>({
    defaultValues: {
      businessName: '',
      contactName: session?.user?.name || '',
      contactEmail: session?.user?.email || '',
      contactPhone: '',
      website: '',
      category: '',
      description: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: DirectoryListingFormData) =>
      entitiesApi.sendDirectoryListingEnquiry({
        businessName: data.businessName,
        contactName: data.contactName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone || undefined,
        website: data.website || undefined,
        category: data.category || undefined,
        description: data.description,
      }),
    onSuccess: () => {
      toast.success('Request sent');
      reset();
      setShowSuccess(true);
      onSuccess?.();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message;
      toast.error(message || 'Failed to send your request');
    },
  });

  if (showSuccess) {
    return (
      <Alert className={cn('border-green-200 bg-green-50 text-green-800', className)}>
        <CheckCircle2 className="text-green-600" />
        <AlertTitle>Request sent</AlertTitle>
        <AlertDescription>
          Thanks. We've received your details and will be in touch soon.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      className={cn('space-y-4', className)}
      autoComplete="off"
    >
      <div>
        <Label htmlFor="businessName">Business name</Label>
        <Input
          id="businessName"
          autoComplete="off"
          {...register('businessName', { required: 'Business name is required' })}
          placeholder="Your business name"
        />
        {errors.businessName && (
          <p className="mt-1 text-sm text-red-600">{errors.businessName.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="contactName">Your name</Label>
        <Input
          id="contactName"
          autoComplete="off"
          {...register('contactName', { required: 'Your name is required' })}
          placeholder="Contact name"
        />
        {errors.contactName && (
          <p className="mt-1 text-sm text-red-600">{errors.contactName.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="contactEmail">Email</Label>
        <Input
          id="contactEmail"
          type="email"
          autoComplete="off"
          {...register('contactEmail', emailValidation)}
          placeholder="you@business.co.nz"
        />
        {errors.contactEmail && (
          <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="contactPhone">Phone (optional)</Label>
        <Input
          id="contactPhone"
          type="tel"
          autoComplete="off"
          {...register('contactPhone')}
          placeholder="+64 21 123 4567"
        />
      </div>

      <div>
        <Label htmlFor="website">Website (optional)</Label>
        <Input
          id="website"
          autoComplete="off"
          {...register('website')}
          placeholder="https://yourbusiness.co.nz"
        />
      </div>

      <div>
        <Label htmlFor="category">What do you do? (optional)</Label>
        <Input
          id="category"
          autoComplete="off"
          {...register('category')}
          placeholder="e.g. Distributor, agency, retailer"
        />
      </div>

      <div>
        <Label htmlFor="description">Tell us about your business</Label>
        <Textarea
          id="description"
          autoComplete="off"
          {...register('description', { required: 'A short description is required' })}
          placeholder="What you offer and how you help food, beverage and consumer goods businesses."
          rows={4}
          className="min-h-[6lh]"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? 'Sending...' : 'Send'}
      </Button>
    </form>
  );
}
